import type { TableV2TableRun } from './state';
import { computed, createModel } from '@preact/signals-react';
import type { Model, ModelConstructor, ReadonlySignal } from '@preact/signals-react';
import type { Stage } from '@thinkalpha/platform-ws-client/contracts/table.js';
import { stringifyKey } from '@thinkalpha/table-client';
import type { Key, SortModel } from '@thinkalpha/table-client';
import { createTable } from 'src/lib/table';
import { makeOptimizedTableFilterKey, makeOptimizedTableSortKey } from 'src/models/TableModel';
import type { TableCreationQuery, TableModel } from 'src/models/TableModel';
import type { FullKeyType } from 'src/types';

export interface TableV2TableQueryModelInit {
    readonly run: ReadonlySignal<TableV2TableRun | null>;
    readonly table: TableModel;
    readonly userViewKey: string;
}

export interface TableV2TableQueryModelShape {
    readonly queryOptions: ReadonlySignal<TableCreationQuery>;
}

export function makeTableV2QueryOptions(
    tableKey: Key | null,
    sort: SortModel | null | undefined,
    filter: string | undefined,
    userViewKey: string,
): TableCreationQuery {
    const stringTableKey = stringifyKey(tableKey);
    const sortModel = sort ?? {};
    return {
        enabled: stringTableKey !== null,
        queryKey: [
            'table-v2-widget',
            stringTableKey,
            userViewKey,
            makeOptimizedTableSortKey(sortModel),
            makeOptimizedTableFilterKey(filter, 'raw-formula'),
        ],
        queryFn: async (_context, debugIdentifier?: string) => {
            if (stringTableKey === null) return undefined;

            const inputStage: Stage = {
                type: 'legacy-table',
                tableKey: stringTableKey,
                rowKey: 'todo-maybe',
            };
            const leafStage: Stage = {
                type: 'output',
                columns: undefined,
                inputStage,
                sort: sortModel,
                filter: filter === undefined ? undefined : { type: 'raw-formula', value: filter },
            };
            const result = await createTable({ leafStage, userViewKey }, undefined, debugIdentifier);
            return { ...result, isScrolling: tableKey?.ex === 'S', fetchMode: undefined } satisfies FullKeyType;
        },
        staleTime: 0,
    };
}

export const TableV2TableQueryModel: ModelConstructor<TableV2TableQueryModelShape, [TableV2TableQueryModelInit]> =
    createModel<TableV2TableQueryModelShape, [TableV2TableQueryModelInit]>((init: TableV2TableQueryModelInit) => {
        const queryOptions = computed(() => {
            const run = init.run.value;
            return makeTableV2QueryOptions(
                run?.tableKey ?? null,
                run?.sort,
                run?.filter,
                run?.userViewKey ?? init.userViewKey,
            );
        });

        init.table.init({
            queryOptions,
            initBounds: { firstRow: 0, windowSize: 100 },
            devtoolsName: 'TableV2Widget/table',
        });

        return { queryOptions };
    });

export type TableV2TableQueryModel = Model<TableV2TableQueryModelShape>;
